import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkey } from '@tanstack/react-hotkeys'
import { useNavigate, useRouterState } from '@tanstack/react-router'

import { useAuth } from '@/lib/auth/AuthProvider'

import { COMMAND_PALETTE_ROUTES, type CommandPaletteRoute, isRouteVisibleOn } from '@/constants/appRoutes'

import View from './CommandPalette.view'

export interface ControllerProps {
  className?: string
}

// Routes hidden on this stage (`hiddenOn`) stay out of the palette, as in the navbar.
const STAGE_PALETTE_ROUTES = COMMAND_PALETTE_ROUTES.filter((route) =>
  isRouteVisibleOn(route, import.meta.env.VITE_STAGE)
)

function routeMatchesPathname(route: CommandPaletteRoute, pathname: string) {
  return pathname === route.to
}

function isAdminRoute(route: CommandPaletteRoute) {
  return route.to.startsWith('/settings') && route.to !== '/settings/changelog'
}

export const Controller = memo(function CommandPaletteController({ className }: ControllerProps) {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const selectedRouteRef = useRef<HTMLButtonElement>(null)

  const routes = useMemo(
    () =>
      STAGE_PALETTE_ROUTES.filter((route) => {
        if (!route.available || (!user?.isAdmin && isAdminRoute(route))) return false

        const normalizedQuery = query.trim().toLowerCase()
        if (!normalizedQuery) return true

        return `${route.label} ${route.to}`.toLowerCase().includes(normalizedQuery)
      }).map((route) => ({ ...route, isCurrentRoute: routeMatchesPathname(route, pathname) })),
    [pathname, query, user?.isAdmin]
  )

  const resetPalette = useCallback(() => {
    setQuery('')
    setSelectedIndex(0)
  }, [])

  const closePalette = useCallback(() => {
    setIsOpen(false)
    resetPalette()
  }, [resetPalette])

  const openPalette = useCallback(() => {
    const currentRouteIndex = STAGE_PALETTE_ROUTES.filter(
      (route) => route.available && (user?.isAdmin ? true : !isAdminRoute(route))
    ).findIndex((route) => routeMatchesPathname(route, pathname))

    setQuery('')
    setSelectedIndex(Math.max(currentRouteIndex, 0))
    setIsOpen(true)
  }, [pathname, user?.isAdmin])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        openPalette()
      } else {
        closePalette()
      }
    },
    [closePalette, openPalette]
  )

  const handleRouteSelect = useCallback(
    (route: CommandPaletteRoute) => {
      closePalette()
      void navigate({ to: route.to })
    },
    [closePalette, navigate]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!(event.target instanceof HTMLInputElement)) return
      if (!routes.length) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setSelectedIndex((currentIndex) => (currentIndex + 1) % routes.length)
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setSelectedIndex((currentIndex) => (currentIndex - 1 + routes.length) % routes.length)
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        const route = routes[selectedIndex]
        if (route) handleRouteSelect(route)
      }
    },
    [handleRouteSelect, routes, selectedIndex]
  )

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    if (!isOpen) return

    selectedRouteRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [isOpen, selectedIndex])

  useHotkey(
    'Mod+K',
    () => {
      if (isOpen) {
        closePalette()
      } else {
        openPalette()
      }
    },
    { preventDefault: true }
  )

  return (
    <View
      className={className}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      query={query}
      onQueryChange={setQuery}
      routes={routes}
      selectedIndex={selectedIndex}
      selectedRouteRef={selectedRouteRef}
      onKeyDown={handleKeyDown}
      onSelectRoute={handleRouteSelect}
    />
  )
})

Controller.displayName = 'CommandPalette_Controller'
